import * as _ from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { useLocationFeatureSwitch } from '@frontend/payments-hooks';
import { Info, SwitchField } from '@frontend/design-system';
import * as styles from '../styles/payment-settings-page.styles';
import { LocationCountChipForToggle } from './location-count-chip-for-toggle';

export type SettingsToggleProps = {
  name: string;
  label: React.ReactNode;
  locationIds: string[];
  switchProps: ReturnType<typeof useLocationFeatureSwitch>['featureSettingSwitchProps'];
  infoText: string;
  hasPermission: boolean;
  className?: string;
  disableLabelClick?: boolean;

  /**
   * If disabled is true, the toggle will be disabled, but always visible
   */
  disabled?: boolean;

  /**
   * Shows the location chip "0 locations" when there are:
   * - no locations enabled
   * - locations selected
   * Default: true
   */
  showChipForNoEnabledLocations?: boolean;
  permissionRoleLabel?: string;
};

export const SettingsToggle = ({
  name,
  label,
  locationIds,
  switchProps,
  infoText,
  disabled = false,
  className,
  hasPermission,
  showChipForNoEnabledLocations = true,
  disableLabelClick = false,
  permissionRoleLabel = '"Payments Billing Manager"',
}: SettingsToggleProps) => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });

  const noPermissionWarning = !hasPermission && (
    <div style={{ marginBottom: '8px' }}>
      {t(
        `You do not have permission to change this setting. Please request {{role}} permissions from your administrator.`,
        { role: permissionRoleLabel }
      )}
    </div>
  );

  if (locationIds.length === 0 && !disabled) {
    return null;
  }

  const labelAndInfoContent = (
    <>
      {label}
      {(!!noPermissionWarning || infoText) && (
        <>
          <Info iconSize={12} type='button' css={styles.ffToggleInfo}>
            {noPermissionWarning}
            {infoText}
          </Info>
        </>
      )}
    </>
  );

  return (
    <div className={className} css={styles.ffToggleContainer}>
      <div css={styles.ffToggleLine}>
        {disableLabelClick && <div css={styles.ffToggleLabel}>{labelAndInfoContent}</div>}
        <SwitchField
          name={name}
          label={!disableLabelClick && labelAndInfoContent}
          labelPlacement='left'
          css={styles.ffSwitch(!disableLabelClick)}
          {...switchProps}
          disabled={switchProps.disabled || !hasPermission || disabled}
        />
      </div>
      {(showChipForNoEnabledLocations || locationIds.length > 0) && (
        <LocationCountChipForToggle enabledLocationIds={locationIds} />
      )}
    </div>
  );
};
